import React, { useMemo } from 'react';
import { Box, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { materialIcon, type MaterialKey } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { DAY_AXIS_CHART_MARGIN, getChartTokens, yAxisWidth } from '../../theme/chartTheme';
import { contentBorders } from '../../theme/createContentTheme';
import { REST_SERIES_KEY, type MaterialDailySplit, type MaterialSeries } from '../../utils/dailyMaterials.helpers';
import { formatDateTime } from '../../utils/numberFormat';
import type { DailyChartType } from './DailyRequestsChart';

interface DailyMaterialChartProps {
  split: MaterialDailySplit;
  type: DailyChartType;
  height?: number;
  ariaLabel: string;
}

/** The categorical series palette without its neutral grey, which marks the remainder. */
const REST_PALETTE_INDEX = 5;

/**
 * Requested kg per day split by the organisation's materials: stacked bars
 * (or stacked areas / one line per material) with a legend grouped by waste
 * category, so "Organic" reads as Cooked rice, Vegetables, …
 */
const DailyMaterialChart: React.FC<DailyMaterialChartProps> = ({ split, type, height = 260, ariaLabel }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const borders = contentBorders(theme);

  const colors = useMemo(() => {
    const named = tokens.palette.filter((_, i) => i !== REST_PALETTE_INDEX);
    const map = new Map<string, string>();
    let next = 0;
    for (const s of split.series) {
      map.set(s.key, s.key === REST_SERIES_KEY ? tokens.palette[REST_PALETTE_INDEX] : named[next++ % named.length]);
    }
    return map;
  }, [split.series, tokens.palette]);

  const seriesName = (s: MaterialSeries) =>
    s.name ?? t('dashboard.daily.split.otherMaterials', { n: s.folded?.length ?? 0 });

  const groups = useMemo(() => {
    const byCategory = new Map<MaterialKey, { category: MaterialKey; totalKg: number; series: MaterialSeries[] }>();
    for (const s of split.series) {
      // The folded remainder spans categories; it closes the legend on its own.
      const category = s.key === REST_SERIES_KEY ? null : s.category;
      if (!category) continue;
      const group = byCategory.get(category) ?? { category, totalKg: 0, series: [] };
      group.totalKg += s.totalKg;
      group.series.push(s);
      byCategory.set(category, group);
    }
    return [...byCategory.values()].sort((a, b) => b.totalKg - a.totalKg);
  }, [split.series]);
  const rest = split.series.find((s) => s.key === REST_SERIES_KEY) ?? null;

  const dayLabel = (value: string) =>
    formatDateTime(`${value}T00:00:00`, i18n.language, { day: 'numeric', month: 'short' });
  const lastKey = split.series[split.series.length - 1]?.key;
  const axisWidth = useMemo(() => {
    let max = 0;
    for (const row of split.rows) {
      // Stacked bars and areas peak at the day total; lines at the largest material.
      const values = split.series.map((s) => Number(row[s.key]) || 0);
      max = Math.max(max, type === 'line' ? Math.max(0, ...values) : values.reduce((a, b) => a + b, 0));
    }
    return yAxisWidth(fmt.compact(max));
  }, [split, type, fmt]);

  const renderTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const items = payload
      .filter((p) => typeof p.value === 'number' && p.value > 0)
      .sort((a, b) => (b.value as number) - (a.value as number));
    const dayTotal = items.reduce((sum, p) => sum + (p.value as number), 0);
    return (
      <Box style={tokens.tooltip.contentStyle} sx={{ minWidth: 180 }}>
        <Typography component="div" sx={{ ...tokens.tooltip.labelStyle, m: 0 }}>
          {dayLabel(String(label))}
        </Typography>
        {items.length === 0 ? (
          <Typography component="div" variant="body2" sx={{ m: 0 }}>
            {fmt.withUnit('mass_kg', 0)}
          </Typography>
        ) : (
          items.map((p) => {
            const s = split.series.find((x) => x.key === p.dataKey);
            return (
              <Box key={String(p.dataKey)} sx={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto', columnGap: 1, alignItems: 'center', py: 0.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: colors.get(String(p.dataKey)) }} />
                <Typography variant="body2" noWrap>
                  <bdi dir="auto">{s ? seriesName(s) : ''}</bdi>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  <bdi>{fmt.withUnit('mass_kg', p.value as number)}</bdi>
                </Typography>
              </Box>
            );
          })
        )}
        {items.length > 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5, pt: 0.5, borderTop: `1px solid ${borders.subtle}` }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.daily.split.dayTotal')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              <bdi>{fmt.withUnit('mass_kg', dayTotal)}</bdi>
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  };

  const axes = (
    <>
      <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
      <XAxis
        dataKey="date"
        tickFormatter={dayLabel}
        tick={tokens.axis.tick}
        axisLine={false}
        tickLine={false}
        tickMargin={8}
        minTickGap={18}
      />
      <YAxis tickFormatter={(v: number) => fmt.compact(v)} tick={tokens.axis.tick} axisLine={false} tickLine={false} width={axisWidth} />
      <Tooltip cursor={tokens.tooltip.cursor} content={renderTooltip} />
    </>
  );
  const summary = split.series.map((s) => `${seriesName(s)}: ${fmt.withUnit('mass_kg', s.totalKg)}`).join(', ');

  const legendRow = (s: MaterialSeries) => (
    <Box
      component="li"
      key={s.key}
      sx={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto auto', columnGap: 1, alignItems: 'center', py: 0.5 }}
    >
      <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: colors.get(s.key) }} />
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        <bdi dir="auto">{seriesName(s)}</bdi>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'end' }}>
        <bdi>{fmt.withUnit('mass_kg', s.totalKg, { scaleMass: true })}</bdi>
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
        <bdi>{fmt.share(split.totalKg > 0 ? s.totalKg / split.totalKg : null)}</bdi>
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
      {/* Time runs left to right in every language; the surrounding text stays RTL. */}
      <Box dir="ltr" role="img" aria-label={`${ariaLabel}. ${summary}`} sx={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          {type === 'line' ? (
            <LineChart data={split.rows} margin={DAY_AXIS_CHART_MARGIN}>
              {axes}
              {split.series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={seriesName(s)}
                  stroke={colors.get(s.key)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={tokens.line.activeDot}
                  {...tokens.animation}
                />
              ))}
            </LineChart>
          ) : type === 'area' ? (
            <AreaChart data={split.rows} margin={DAY_AXIS_CHART_MARGIN}>
              {axes}
              {split.series.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={seriesName(s)}
                  stackId="materials"
                  stroke={colors.get(s.key)}
                  strokeWidth={1.5}
                  fill={colors.get(s.key)}
                  fillOpacity={0.35}
                  activeDot={tokens.line.activeDot}
                  {...tokens.animation}
                />
              ))}
            </AreaChart>
          ) : (
            <BarChart data={split.rows} margin={DAY_AXIS_CHART_MARGIN}>
              {axes}
              {split.series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={seriesName(s)}
                  stackId="materials"
                  fill={colors.get(s.key)}
                  maxBarSize={tokens.bar.maxBarSize}
                  radius={s.key === lastKey ? tokens.bar.radius : 0}
                  {...tokens.animation}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fill, minmax(240px, 1fr))' },
          columnGap: 3,
          rowGap: 1.5,
        }}
      >
        {groups.map((group) => {
          const Icon = materialIcon(group.category);
          return (
            <Box key={group.category} sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pb: 0.5, borderBottom: `1px solid ${borders.subtle}` }}>
                <Icon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flex: 1, minWidth: 0 }} noWrap>
                  {t(`materials.${group.category}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  <bdi>{fmt.withUnit('mass_kg', group.totalKg, { scaleMass: true })}</bdi>
                </Typography>
              </Box>
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {group.series.map((s) => legendRow(s))}
              </Box>
            </Box>
          );
        })}
        {rest ? (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pb: 0.5, borderBottom: `1px solid ${borders.subtle}` }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }} noWrap>
                {t('dashboard.daily.split.smaller')}
              </Typography>
            </Box>
            <MuiTooltip title={(rest.folded ?? []).join(' · ')} placement="top">
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {legendRow(rest)}
              </Box>
            </MuiTooltip>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default DailyMaterialChart;
